<script setup lang="ts">
	import { useDebounceFn } from '@vueuse/core'
	import type { SelectValue } from 'ant-design-vue/es/select'

	const prop = defineProps<{
		name: string
		purchaseId: string
		placeholder?: string
		disabled?: boolean
	}>()
	const modelValue = defineModel<string | null>('value', { default: null, required: false })
	// Staff Query
	const staffRecords = ref<SelectOptionContext>([])
	const handleDeselect = () => fetchStaff({ search: '', page: 1 })
	const elId = useId()
	const emits = defineEmits<{
		(event: 'handleChange', result: EmployeesResponse | null): void
	}>()
	const { extractStaffUsingId, fetchStaff, staffList, scrollingItem, paginationFilter } = useStaffList({
		purchaseId: () => prop.purchaseId
	})
	const searchValue = useDebounceFn(async (val: string) => {
		staffRecords.value = await fetchStaff({ search: val, page: 1 })
	}, 1000)
	const selectVal = shallowRef<string | null>(null)
	const sendRecord = (e: SelectValue | string) => {
		emits('handleChange', extractStaffUsingId(e as string, staffList.value))
		modelValue.value = String(e)
	}

	onMounted(() => {
		if (!staffList.value.length) {
			fetchStaff({ page: 1 })
		}
	})

	watch(
		staffList,
		() => {
			staffRecords.value = staffList.value
		},
		{ immediate: true }
	)
</script>

<template>
	<a-select
		v-model:value="selectVal"
		:options="staffRecords"
		:name
		:aria-label="`${name}-${elId}`"
		:data-testid="`${name}-${elId}`"
		:placeholder="placeholder"
		class="custom-select h-full w-full min-w-[100px]"
		show-search
		:virtual="false"
		:filter-option="false"
		:not-found-content="null"
		:disabled="disabled"
		allow-clear
		:loading="paginationFilter.loading"
		@popup-scroll="scrollingItem"
		@search="searchValue"
		@deselect="() => handleDeselect()"
		@change="e => sendRecord(e)"
	>
		<template #option="content">
			<div class="inline-flex items-center justify-between gap-x-2">
				<a-checkbox />
				<div class="text-sm text-gray-700">
					<span>{{ content.label }}</span>
					<p class="text-muted text-xs font-medium lowercase">
						{{ content?.email ?? '' }}
					</p>
				</div>
			</div>
		</template>
	</a-select>
</template>
